<script lang="ts">
  import globalAxios from "axios";
  import Form from "$components/form/Form/Form.svelte";
  import InputField from "$components/form/Fields/InputField.svelte";
  import RichTextField from "$components/form/Fields/RichTextField.svelte";
  import LabelsListManager from "$components/+shared/LabelsListManager.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import Separator from "app/components/+shared/Separator/Separator.svelte";
  import api from "$utils/api";
  import { createEventDispatcher } from "svelte";
  import { alerts } from "$stores/alert";
  import { StatusCodes } from "$utils/statusCodes";
  import { communitySlug } from "$stores/community";
  import { forum } from "$stores/forum";
  import { labels } from "$stores/labels";
  import { YDoc } from "app/components/editor";
  import { track } from "app/utils/analytics";
  import { Pencil } from "$components/+shared/OVIcon";
  import type { Thread } from "models/forums";
  import type { ForumSpace, LabelSerialiser } from "@outverse-video/outverse-api-client-lib";
  import type { User } from "$stores/user";

  export let thread: Thread | undefined = undefined;
  export let forumSpace: ForumSpace | undefined = undefined;
  export let user: User;

  let submitting: boolean = false;
  let selectedLabels: LabelSerialiser[] = thread ? thread.labels.map((uuid) => $labels[uuid]) : [];

  const dispatch = createEventDispatcher<{
    createThread: Thread;
    updateThread: Thread;
    close: void;
  }>();

  interface FormData {
    title: string;
    content: {
      json: string;
      text: string;
      empty: boolean;
    };
  }

  // create ydoc for storing new thread drafts
  const ydoc = YDoc({
    user,
    clearOnLoad: !!thread?.uuid, // when editing thread clear previous draft
    documentName: `${user?.uuid}-${forumSpace?.uuid}-${thread?.uuid}`,
  });

  async function onSubmit(event: CustomEvent<Omit<FormData, "author">>) {
    submitting = true;

    const form = event.detail;
    if (!forumSpace?.uuid) {
      alerts.error("Select a forum");
      submitting = false;
      return;
    }

    const formData = {
      title: form.title,
      content: form.content.json,
      text: form.content.text,
      labels: selectedLabels.map((label) => label.uuid),
    };

    if (form.content.empty) {
      track("Create Update Thread Modal empty");
      alerts.error(`Please add content before submitting.`);
      submitting = false;
      return;
    }

    try {
      let resp;
      if (thread) {
        track("Clicked Update Thread Button");

        resp = await api.communitiesThreadsPartialUpdate({
          communitySlug: $communitySlug,
          uuid: thread.uuid,
          patchedForumThread: {
            ...(formData as any),
            forumSpace: forumSpace.uuid,
          },
        });
        // We've got a deep incompatibility between these types, due to Author !== ReplierSerializer.
        // @ts-ignore
        dispatch("updateThread", resp.data);
        alerts.success(`${form.title} thread has been updated.`);
      } else {
        track("Clicked Create Thread Button");
        resp = await api.communitiesThreadsCreate({
          communitySlug: $communitySlug,
          forumThread: {
            forumSpace: forumSpace.uuid,
            ...formData,
          } as any,
        });
        resp = await api.communitiesThreadsRetrieve({
          communitySlug: $communitySlug,
          uuid: resp.data.uuid,
        });
        const thread = resp.data;
        $forum.threads[thread.forumThread.uuid] = {
          ...thread.forumThread,
          myPermissions: thread.myPermissions,
        };
        $forum.threadIDs = [thread.forumThread.uuid, ...$forum.threadIDs];
        dispatch("createThread", thread.forumThread);
      }
      ydoc.clearLocalStorage();
    } catch (error: any) {
      let msg = "An error occured, please try submitting your thread again.";
      track("Create/Update Thread Failed");

      if (globalAxios.isAxiosError(error)) {
        if (error.response) {
          // server-reported error
          if (error.response.status === StatusCodes.TOO_MANY_REQUESTS) {
            msg = "Activity timeout.";
          } else if (error.response.status === StatusCodes.BAD_REQUEST) {
            msg = "Validation failed, please check your thread and try again.";
          } else {
            // forbidden/not-found
            msg = `Unable to ${thread ? "update" : "add"} thread for this forum space.`;
          }
        }
      }
      alerts.error(msg);
    }
    submitting = false;
  }
</script>

<Form
  name="create-update-thread"
  id="create-update-thread"
  class="flex grow flex-col overflow-hidden px-3"
  on:submit={onSubmit}
  on:keypress={(e) => {
    if (e.key === "Enter") e.preventDefault();
  }}
  on:error={() => track("Create/Update Thread Empty")}
  defaultValues={thread
    ? {
        title: thread.title,
        content: { json: thread.content, text: "" },
      }
    : undefined}
>
  <InputField
    autoFocus
    name="title"
    variant="secondary"
    inputProps={{ placeholder: "Thread title" }}
    validationRules={{ required: "This is required." }}
    textSize="text-lg"
  />
  {#if $ydoc}
    <RichTextField
      {user}
      {ydoc}
      name="content"
      wrapperClassname="mb-2 grow overflow-y-auto pl-5 pr-3"
    />
  {/if}

  <Separator bgColor="bg-black/10 dark:bg-white/8" />

  <div class="flex justify-between py-3">
    {#if forumSpace?.labelsEnabled}
      <LabelsListManager bind:selectedLabels canEdit />
    {/if}

    <span class="ml-auto">
      <Button
        id={"create-update-thread-button"}
        variant="primary"
        disabled={submitting}
        htmlButtonType="submit"
        label={submitting ? "Submitting..." : thread ? "Update thread" : "Create thread"}
        iconRight={Pencil}
      />
    </span>
  </div>
</Form>
